import React from 'react';
import * as Sentry from '@sentry/react';
import { Messages } from '@ais-ui/locale-management';
import { Scope } from '@sentry/react';

// If your ErrorBoundary doesn't accept any props or you want to specify what props it accepts, you can define them here.
interface ErrorBoundaryProps {
  // Define any props your ErrorBoundary might take, for example:
  children: React.ReactNode;
  messages: Messages;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.withScope((scope: Scope) => {
      const sentryExtras = {
        componentStack: errorInfo.componentStack,
        ...errorInfo,
      };

      scope.setExtras(sentryExtras);
      scope.setTag('context', 'main-error-boundary');
      scope.setTag('window.type', 'main');
      Sentry.captureException(error);
    });
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="error-container flex flex-col justify-center items-center h-3/5 w-full space-y-4">
            <div className="relative">
              <img
                className="max-w-xs max-h-96"
                src="500_internal_server_error_bro.png"
                alt="Error"
              />
              <a
                href="https://storyset.com/internet"
                className="absolute bottom-0 right-0 text-xs text-muted-foreground bg-card bg-opacity-75 p-1"
              ></a>
            </div>
            <h1 className="text-lg">
              {this.props.messages.errorBoundaryTitle}
            </h1>
            <p className="text-sm text-muted-foreground">
              {this.props.messages.errorBoundaryText}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
